import React, { useState } from "react";
import { getFieldValidation } from "../../validationRules/fieldValidationRules";

// Helper to sort options with "Other" always at the end
const sortOptionsWithOtherLast = (options) => {
  if (!options) return options;
  return [...options].sort((a, b) => {
    if (a.isOther && !b.isOther) return 1;
    if (!a.isOther && b.isOther) return -1;
    return 0;
  });
};

const UserForm = ({ formData, onChange, formConfig, errors = {} }) => {
  const [fieldErrors, setFieldErrors] = useState({});

  if (!formConfig || !formConfig.fields) {
    return null;
  }

  // Sort fields by order
  const sortedFields = [...formConfig.fields].sort((a, b) => {
    return (a.order || 0) - (b.order || 0);
  });
  // Handle field value change
  const handleFieldChange = (field, value) => {
    const validation = getFieldValidation(field.type);

    let error = validation.validate(value);

    if (field.required && field.type === "select" && field.options) {
      const selectedOption = field.options.find((opt) => opt.value === value);
      if (selectedOption && selectedOption.allowSubmit === false) {
        error = "בחירה זו אינה מאפשרת שליחת הטופס";
      }
    }

    // Update errors
    setFieldErrors((prev) => ({
      ...prev,
      [field.id]: error,
    }));

    // Pass transformed value
    const transformedValue = validation.transform(value);
    onChange({
      target: {
        name: field.id,
        value: transformedValue,
      },
    });

    // Clear "other" text field when changing select option away from "other"
    if (field.type === "select") {
      const selectedOption = field.options?.find((opt) => opt.value === value);
      const otherTextKey = `${field.id}_otherText`;
      if (!selectedOption?.isOther) {
        // Clear the other text field
        setFieldErrors((prev) => ({
          ...prev,
          [otherTextKey]: null,
        }));
        onChange({
          target: {
            name: otherTextKey,
            value: "",
          },
        });
      }
    }
  };
  // Handle file field change
  const handleFileChange = (field, file) => {
    if (!file) {
      onChange({
        target: {
          name: field.id,
          value: null,
        },
      });
      setFieldErrors((prev) => ({
        ...prev,
        [field.id]: null,
      }));
      return;
    }

    // File validation
    let error = null;

    // Check file type
    if (field.fileTypes && field.fileTypes !== "any") {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf(".") + 1);

      let allowedExtensions = [];
      switch (field.fileTypes) {
        case "documents":
          allowedExtensions = ["pdf", "doc", "docx"];
          break;
        case "images":
          allowedExtensions = ["jpg", "jpeg", "png", "gif"];
          break;
        case "pdf":
          allowedExtensions = ["pdf"];
          break;
        case "images-documents":
          allowedExtensions = [
            "jpg",
            "jpeg",
            "png",
            "gif",
            "pdf",
            "doc",
            "docx",
          ];
          break;
        default:
          allowedExtensions = [];
      }

      if (
        allowedExtensions.length > 0 &&
        !allowedExtensions.includes(fileExtension)
      ) {
        error = `סוג קובץ לא מורשה. קבצים מותרים: ${allowedExtensions.join(
          ", "
        )}`;
      }
    }

    // Check file size
    if (!error && field.maxFileSize) {
      const maxSizeMB = parseInt(field.maxFileSize);
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        error = `הקובץ גדול מדי. גודל מקסימלי: ${maxSizeMB} מגה-בייט`;
      }
    }

    // Update errors
    setFieldErrors((prev) => ({
      ...prev,
      [field.id]: error,
    }));

    // If no error, pass the file
    if (!error) {
      onChange({
        target: {
          name: field.id,
          value: file,
        },
      });
    }
  };

  // Get file accept types for input
  const getFileAcceptTypes = (fileTypes) => {
    if (!fileTypes || fileTypes === "any") {
      return "*/*";
    }

    switch (fileTypes) {
      case "documents":
        return ".pdf,.doc,.docx";
      case "images":
        return ".jpg,.jpeg,.png,.gif";
      case "pdf":
        return ".pdf";
      case "images-documents":
        return ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx";
      default:
        return "*/*";
    }
  };
  return (
    <>
      {sortedFields.map((field) => {
        const error = fieldErrors[field.id] || errors[field.id];

        // Handle subtitle fields differently - they are display-only
        if (field.type === "subtitle") {
          const getFontSize = (size) => {
            const sizes = {
              "-2": "clamp(14px, 3vw, 18px)",
              "-1": "clamp(16px, 4vw, 22px)",
              "0": "clamp(18px, 5vw, 26px)",
              "1": "clamp(20px, 6vw, 32px)",
              "2": "clamp(24px, 7vw, 38px)",
            };
            return sizes[String(size)] || sizes["0"];
          };

          return (
            <h3
              key={field.id}
              className="form-subtitle"
              style={{
                textAlign: field.subtitleAlignment || "right",
                fontSize: getFontSize(field.subtitleSize ?? 0),
                color: field.subtitleColor || "#2c3e50",
              }}
            >
              {field.label}
            </h3>
          );
        }

        // Handle link fields - they are display-only clickable links
        if (field.type === "link") {
          const displayText = field.linkDisplayMode === "url"
            ? field.linkUrl
            : field.label;

          const getLinkFontSize = (size) => {
            const sizes = {
              "-1": "clamp(12px, 3vw, 14px)",
              "0": "clamp(14px, 4vw, 16px)",
              "1": "clamp(16px, 5vw, 20px)",
            };
            return sizes[String(size)] || sizes["0"];
          };

          return (
            <div
              key={field.id}
              className="form-link-wrapper"
              style={{ textAlign: field.linkAlignment || "right" }}
            >
              <a
                href={field.linkUrl || "#"}
                target={field.linkOpenNewTab ? "_blank" : "_self"}
                rel={field.linkOpenNewTab ? "noopener noreferrer" : undefined}
                className="form-link"
                style={{
                  fontSize: getLinkFontSize(field.linkSize ?? 0),
                  fontWeight: field.linkBold ? "bold" : "normal",
                  fontStyle: field.linkItalic ? "italic" : "normal",
                }}
                onClick={(e) => { if (!field.linkUrl) e.preventDefault(); }}
              >
                {displayText || "קישור"}
              </a>
            </div>
          );
        }

        const alignment = field.fieldAlignment || field.labelAlignment || "right";

        return (
          <div
            key={field.id}
            className={`form-group${
              field.type === "checkbox" ? " checkbox-group" : ""
            }${alignment === "left" ? " form-group-ltr" : ""}`}
          >
            {field.type === "checkbox" ? (
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  id={field.id}
                  name={field.id}
                  checked={!!formData[field.id]}
                  onChange={(e) => handleFieldChange(field, e.target.checked)}
                  required={field.required}
                  className={error ? "form-input-error" : ""}
                />
                {field.checkboxText || field.label}
                {field.required && <span style={{ color: "#e74c3c" }}> *</span>}
              </label>
            ) : (
              <>
                <label
                  htmlFor={field.id}
                >
                  {field.label}
                  {field.required && (
                    <span style={{ color: "#e74c3c" }}> *</span>
                  )}
                </label>
                {field.type === "file" ? (
                  <input
                    type="file"
                    id={field.id}
                    name={field.id}
                    onChange={(e) => handleFileChange(field, e.target.files[0])}
                    required={field.required}
                    className={error ? "form-input-error" : ""}
                    accept={getFileAcceptTypes(field.fileTypes)}
                  />
                ) : field.type === "select" && field.isMultiSelect ? (
                  // Multi-select with checkboxes
                  <div className={`multi-select-container ${error ? "form-input-error" : ""}`}>
                    {sortOptionsWithOtherLast(field.options)?.map((opt) => {
                      const currentValues = Array.isArray(formData[field.id]) ? formData[field.id] : [];
                      const isChecked = currentValues.includes(opt.value);

                      // "Other" option with inline text field
                      if (opt.isOther) {
                        const otherTextKey = `${field.id}_otherText`;
                        const otherError = fieldErrors[otherTextKey] || errors[otherTextKey];
                        return (
                          <div key={opt.value} className="multi-select-other-row">
                            <label className="multi-select-option multi-select-other-option">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const newValues = e.target.checked
                                    ? [...currentValues, opt.value]
                                    : currentValues.filter((v) => v !== opt.value);
                                  onChange({
                                    target: {
                                      name: field.id,
                                      value: newValues,
                                    },
                                  });
                                  // Clear other text when unchecking
                                  if (!e.target.checked) {
                                    setFieldErrors((prev) => ({
                                      ...prev,
                                      [otherTextKey]: null,
                                    }));
                                    onChange({
                                      target: {
                                        name: otherTextKey,
                                        value: "",
                                      },
                                    });
                                  }
                                }}
                              />
                              <span>{opt.label}</span>
                            </label>
                            {isChecked && opt.showTextField !== false && (
                              <input
                                type="text"
                                className={`multi-select-other-input ${otherError ? "form-input-error" : ""}`}
                                id={otherTextKey}
                                name={otherTextKey}
                                value={formData[otherTextKey] || ""}
                                onChange={(e) => {
                                  let otherFieldError = null;
                                  if (opt.textFieldRequired && !e.target.value.trim()) {
                                    otherFieldError = "נא לפרט";
                                  }
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    [otherTextKey]: otherFieldError,
                                  }));
                                  onChange({
                                    target: {
                                      name: otherTextKey,
                                      value: e.target.value,
                                    },
                                  });
                                }}
                                placeholder="פרט כאן..."
                                required={opt.textFieldRequired}
                              />
                            )}
                            {otherError && <span className="field-error multi-select-other-error">{otherError}</span>}
                          </div>
                        );
                      }

                      return (
                        <label key={opt.value} className="multi-select-option">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const newValues = e.target.checked
                                ? [...currentValues, opt.value]
                                : currentValues.filter((v) => v !== opt.value);
                              onChange({
                                target: {
                                  name: field.id,
                                  value: newValues,
                                },
                              });
                            }}
                          />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : field.type === "select" ? (
                  // Regular single-select dropdown
                  <>
                    <select
                      id={field.id}
                      name={field.id}
                      value={formData[field.id] || ""}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      required={field.required}
                      className={error ? "form-input-error" : ""}
                    >
                      <option value="">{field.placeholder || "בחר"}</option>
                      {sortOptionsWithOtherLast(field.options)?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {/* Show text field when "Other" option is selected */}
                    {(() => {
                      const selectedOption = field.options?.find(
                        (opt) => opt.value === formData[field.id]
                      );
                      if (selectedOption?.isOther && selectedOption?.showTextField !== false) {
                        const otherTextKey = `${field.id}_otherText`;
                        const otherError = fieldErrors[otherTextKey] || errors[otherTextKey];
                        return (
                          <div className="other-text-field">
                            <input
                              type="text"
                              id={otherTextKey}
                              name={otherTextKey}
                              value={formData[otherTextKey] || ""}
                              onChange={(e) => {
                                // Validate if required
                                let error = null;
                                if (selectedOption.textFieldRequired && !e.target.value.trim()) {
                                  error = "נא לפרט";
                                }
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  [otherTextKey]: error,
                                }));
                                onChange({
                                  target: {
                                    name: otherTextKey,
                                    value: e.target.value,
                                  },
                                });
                              }}
                              placeholder="פרט כאן..."
                              required={selectedOption.textFieldRequired}
                              className={otherError ? "form-input-error" : ""}
                            />
                            {otherError && <span className="field-error">{otherError}</span>}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </>
                ) : (
                  <input
                    type={["fullName", "firstName", "lastName"].includes(field.type) ? "text" : field.type}
                    id={field.id}
                    name={field.id}
                    value={formData[field.id] || ""}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                    className={error ? "form-input-error" : ""}
                  />
                )}
              </>
            )}
            {error && <span className="field-error">{error}</span>}
          </div>
        );
      })}
    </>
  );
};

export default UserForm;
